import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Landmark, CalendarClock, CheckCheck, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const timeAgo = (iso) => {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "baru saja";
  if (m < 60) return `${m} mnt lalu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} jam lalu`;
  return `${Math.floor(h / 24)} hari lalu`;
};

export function NotificationBell() {
  const [data, setData] = useState({ items: [], unread_count: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const openItem = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    setOpen(false);
    load();
    if (n.link) navigate(n.link);
  };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-sky-50 transition-colors" data-testid="btn-notifications" aria-label="Notifikasi">
          <Bell className={`h-5 w-5 ${data.unread_count ? "text-sky-700 bell-ring" : "text-slate-600"}`} />
          {data.unread_count > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-badge">{data.unread_count > 9 ? "9+" : data.unread_count}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 rounded-2xl border-sky-100 shadow-xl" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b border-sky-100 flex items-center justify-between">
          <div className="font-bold text-slate-900">Notifikasi</div>
          {data.unread_count > 0 && <button onClick={readAll} className="text-xs text-sky-700 font-semibold inline-flex items-center gap-1 hover:underline" data-testid="btn-read-all"><CheckCheck className="h-3.5 w-3.5" /> Tandai semua dibaca</button>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <div className="py-10 text-center text-sm text-slate-500" data-testid="notifications-empty">Belum ada notifikasi.</div>}
          {data.items.map((n, i) => (
            <button key={n.id} onClick={() => openItem(n)} className={`w-full text-left px-4 py-3 flex gap-3 border-b border-sky-50 last:border-0 hover:bg-sky-50/60 transition-colors ${n.read ? "" : "bg-sky-50/40"}`} data-testid={`notification-item-${i}`}>
              <span className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${n.type === "payment" ? "bg-emerald-100 text-emerald-700" : n.type === "reset_request" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>
                {n.type === "payment" ? <Landmark className="h-4 w-4" /> : n.type === "reset_request" ? <KeyRound className="h-4 w-4" /> : <CalendarClock className="h-4 w-4" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2"><span className="text-sm font-semibold text-slate-800 truncate">{n.title}</span>{!n.read && <span className="h-2 w-2 rounded-full bg-sky-500 shrink-0" />}</span>
                <span className="block text-xs text-slate-600 mt-0.5 leading-snug">{n.message}</span>
                <span className="block text-[11px] text-slate-400 mt-1">{timeAgo(n.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
